import React, { useCallback, useEffect } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import CloseButton from '~/shared/components/CloseButton';
import FableticsModal from '~/shared/components/FableticsModal';
import {
  desktop,
  mobile,
  ProductImage as BaseProductImage,
  useBreakpoint,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { defineMessages, useIntl } from '~/techstyle-shared/react-intl';

import { useTrackSpeedySignupPageView } from '../../utils/useTrackSpeedySignupPageView';
import ProductOverlay from '../ProductOverlay';
import SpeedySignUpPanels from '../SpeedySignupPanels';

const speedySignupLayoutEnhanced = config.get(
  'public.brand.speedySignupLayoutEnhanced'
);

const messages = defineMessages({
  modalTitle: {
    id: 'speedy_signup.title',
    defaultMessage: 'Speedy signup',
  },
});

const StyledFableticsModal = styled(FableticsModal)`
  margin: 24px;
  ${mobile`
    margin: 16px;
    & > [data-modal-dialog] {
      flex: 1 1 auto;
      & > * {
        flex: 1 1 auto;
      }
    }
  `}
`;

const maxHeightStyles = css`
  height: 100%;
  /* min-height: fit-content; */
  /* max-height: calc(100vh - 48px); */
`;

const contentWrapperStyles = css`
  ${maxHeightStyles}
  border-radius: 16px;
  overflow: hidden;
  flex-flow: row nowrap;
  align-items: stretch;
  ${mobile`
    flex-flow: column nowrap;
  `}
  & > * {
    @media (min-width: ${({ theme }) => theme.breakpoints.tablet.minWidth}px) {
      flex: 0 0 50%;
    }
  }
`;

const dialogStyle = css`
  width: 800px;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
  max-height: calc(100vh - 96px);
  ${desktop`
    max-height: 600px;
  `}
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet.maxWidth}px) {
    width: 100%;
    overflow-y: auto;
  }
`;

const ProductImage = styled(BaseProductImage)`
  height: 100%;

  picture {
    height: 100%;
  }

  img {
    height: 100%;
    object-fit: cover;
  }
`;

const ProductImageWrapper = styled.section`
  ${({ theme }) => `
    @media (min-width: ${theme.breakpoints.tablet.minWidth}px) and (max-width: ${theme.breakpoints.tablet.maxWidth}px) {
      height: 100%;
      max-height: 600px;
      img {
        object-fit: cover;
      }
    }
  `}
  object-fit: contain;
  position: relative; // for product detail overlay
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet.minWidth}px) {
    flex: 0 0 50%;
  }

  ${mobile`
    flex: 1 0 100%;
    width: 100%;
    overflow: hidden;
    max-height: 200px;
  `}
`;

const ModalCloseButton = styled(CloseButton)`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 24px;
  color: ${({ theme }) => theme.colors.closeButton};
`;

const ProductOverlayWrapper = styled.div`
  position: absolute;
  bottom: 0;
  padding: 20px;
  width: 100%;
`;

const ProductOverlayContent = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius};
  background: ${({ theme }) => theme.colors.backgroundOverlay};
  padding: 12px;
`;

const transitionProps = {
  enter: { opacity: 1, transform: 'scale(1, 1)' },
  from: { opacity: 0, transform: 'scale(0.5, 0.5)' },
  leave: { opacity: 0, transform: 'scale(0.5, 0.5)' },
};

const SignupPanels = styled(SpeedySignUpPanels)`
  display: flex;
  align-self: stretch;
  width: 100%;
  overflow: hidden;
  ${maxHeightStyles}
  & > *,
  [data-slider],
  [data-panel] {
    width: inherit;
    height: inherit;
    min-height: inherit;
    max-height: inherit;
    overflow: hidden;
    ${mobile`
      width: 100%;
    `}
  }
`;

const SpeedySignupModal = (props) => {
  const { isOpen, onExit } = props;
  const intl = useIntl();
  const { isPlusSizeSelected, product } = useProductDetailContext();
  const bannerImageBreakpoints = [{ minWidth: 0, pictureWidth: 400 }];
  const { isDesktop } = useBreakpoint();

  // enhanced layout always shows the overlay over the image, non-enhanced overlay it moves on mobile
  // see SpeedySignupPanels for the mobile render for non-enhanced layouts
  const showProductOverlay = speedySignupLayoutEnhanced || isDesktop;
  const trackSpeedySignupPageView = useTrackSpeedySignupPageView();

  const onSignupSuccess = useCallback(() => {
    if (isOpen) {
      onExit();
    }
  }, [isOpen, onExit]);

  useEffect(() => {
    if (isOpen) {
      trackSpeedySignupPageView();
    }
  }, [isOpen, trackSpeedySignupPageView]);

  return (
    <StyledFableticsModal
      defaultOpen={false}
      dialogStyle={dialogStyle}
      overlayClickExits
      showCloseButton
      closeButton={<ModalCloseButton data-autotag="SpeedySignupModal-Close" />}
      transitionEnter={transitionProps.enter}
      transitionFrom={transitionProps.from}
      transitionLeave={transitionProps.leave}
      title={intl.formatMessage(messages.modalTitle)}
      contentWrapperStyles={contentWrapperStyles}
      data-autotag="SpeedySignupModal"
      {...props}
    >
      {showProductOverlay && (
        <>
          <ProductImageWrapper data-autotag="SpeedySignupModal-PDP-Product">
            <ProductImage
              breakpoints={bannerImageBreakpoints}
              type={isPlusSizeSelected ? 'plus' : 'default'}
              disableHover
            />
            <ProductOverlayWrapper>
              <ProductOverlayContent>
                <ProductOverlay product={product} />
              </ProductOverlayContent>
            </ProductOverlayWrapper>
          </ProductImageWrapper>
        </>
      )}
      <SignupPanels
        data-autotag="SpeedySignupModal-SignupPanel"
        onSignupSuccess={onSignupSuccess}
      />
    </StyledFableticsModal>
  );
};
SpeedySignupModal.propTypes = {
  isOpen: PropTypes.bool,
  onExit: PropTypes.func,
};
export default SpeedySignupModal;
